import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchMessages, fetchConversationStatus, addMessage, createConversation } from './http';
import useWebsockets from 'utils/hooks/useWebsockets';
import { setAtomicUpdate } from 'redux/modules/job-detail/duck';
import { sendEventToSegment } from 'redux/modules/analytics';
import {
  CHAT_CONVERSATION_OPEN,
  CHAT_CONVERSATION_CREATE,
  CHAT_CONVERSATION_MESSAGES_LOADED,
  CHAT_CONVERSATION_SEND,
} from 'redux/modules/analytics/events';

export function useConversation(conversationId, journeyId) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const dispatch = useDispatch();
  const handleCreateConversation = useCallback(async () => {
    if (!journeyId) return;

    try {
      setLoading(true);
      const { id } = await createConversation(journeyId);
      dispatch(setAtomicUpdate({ conversation_id: id }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      sendEventToSegment(CHAT_CONVERSATION_CREATE);
    }
  }, [journeyId, dispatch]);

  const handleSendMessage = useCallback(
    async (message) => {
      if (!message) return;

      try {
        setSendingMessage(true);
        await addMessage(conversationId, message);
        sendEventToSegment(CHAT_CONVERSATION_SEND, { conversationId });
      } catch (e) {
        console.error(e);
      } finally {
        setSendingMessage(false);
      }
    },
    [conversationId]
  );

  const handleRealTimeMessages = useCallback(
    (event) => {
      if (!event || event.conversation_id !== conversationId) return;
      const { type, driver_name, agent_name, message } = event;

      if (!['agent_message', 'driver_message'].includes(type)) return;

      return setMessages((messages) => [
        ...messages,
        buildMessage({ type, agent_name, driver_name, message }),
      ]);
    },
    [conversationId]
  );

  useWebsockets({
    channel: 'notifications',
    event: 'user-notifications',
    eventHandler: handleRealTimeMessages,
  });

  useEffect(() => {
    const loadMessages = async () => {
      if (!conversationId) return;
      setLoading(true);
      try {
        const [messagesResponse, statusResponse] = await Promise.all([
          fetchMessages(conversationId),
          fetchConversationStatus(conversationId),
        ]);

        setStatus(statusResponse);

        const { results } = messagesResponse || {};

        if (Array.isArray(results)) {
          setMessages(results);
          sendEventToSegment(CHAT_CONVERSATION_MESSAGES_LOADED);
          return;
        }

        setMessages([]);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    sendEventToSegment(CHAT_CONVERSATION_OPEN);
    loadMessages();
  }, [conversationId]);

  return {
    messages,
    status,
    loading,
    sendingMessage,
    handleSendMessage,
    handleCreateConversation,
  };
}

/**
 *
 * @param type {String}
 * @param agent_name {String}
 * @param driver_name {String}
 * @param message {String}
 * @returns {{sender: string, created_at: string, message: string}}
 */
function buildMessage({ type, agent_name, driver_name, message }) {
  const senderTypes = {
    agent_message: { name: agent_name ? agent_name : 'Ontruck', agent: true },
    driver_message: { name: driver_name, agent: false },
  };

  return {
    sender: senderTypes[type],
    message: message,
    created_at: new Date().toISOString(),
  };
}
